import { Box, Typography } from '@mui/material';
import { FormTitleBox, LoadingSkeleton, ConfirmationDialog, PaginationTable, TableBodySkeleton } from '../../../common/components';
import LotsTableView from './components/LotsTableView';
import LotDialog from './components/LotDialog';
import LotsPlanView from './components/LotsPlanView';
import LotsDashboardView from './components/LotsDashboardView';
import LotsHeaderControls from './components/LotsHeaderControls';
import { useLotsTable } from './hooks/useLotsTable';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useCallback, useMemo, useState } from 'react';
import usePermissions from '../../../hooks/usePermissions';
import {
  exportLotsCompleteDatabaseToExcel,
  exportSingleLotToPdf,
  getLotResolvedProject,
  getLotResolvedContractNumber,
  getLotResolvedConstructionType,
} from './utils/lotExports';

const LotsTable = () => {
  const [view, setView] = useState('plan');
  const { permissions } = usePermissions();
  const canEditLots = Boolean(permissions?.canEditLots);
  const {
    allotments,
    allLots,
    allAllotments,
    selectedAllotmentLots,
    projects,
    contracts,
    constructionTypes,
    isAllLotsLoading,
    monthlyProgressOverview,
    isMonthlyProgressOverviewLoading,
    selectedAllotment,
    selectedReferenceStatus,
    selectedContractFilter,
    referenceStatusOptions,
    contractOptions,
    handleSelectAllotment,
    handleReferenceStatusChange,
    handleContractFilterChange,
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedLot,
    selectedProject,
    selectedContract,
    subscribers,
    deleteConfirmOpen,
    lotToDelete,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleSave,
    handleEdit,
    handleUpdateLot,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  } = useLotsTable();

  const selectedAllotmentData = useMemo(
    () => allotments.find((item) => item.uuid === selectedAllotment),
    [allotments, selectedAllotment]
  );

  const projectsMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project.uuid_lot) {
        acc[project.uuid_lot] = project;
      }
      return acc;
    }, {});
  }, [projects]);

  const allotmentsById = useMemo(() => {
    const source = allAllotments.length > 0 ? allAllotments : allotments;
    return source.reduce((acc, allotment) => {
      if (allotment?.uuid) {
        acc[allotment.uuid] = allotment;
      }
      return acc;
    }, {});
  }, [allAllotments, allotments]);

  const contractsById = useMemo(
    () =>
      contracts.reduce((acc, contract) => {
        if (contract?.uuid) {
          acc[contract.uuid] = contract;
        }
        return acc;
      }, {}),
    [contracts]
  );

  const constructionTypeNamesById = useMemo(
    () =>
      constructionTypes.reduce((acc, type) => {
        if (type?.uuid) {
          acc[type.uuid] = type.name || '-';
        }
        return acc;
      }, {}),
    [constructionTypes]
  );

  const handleDownloadFullDatabase = useCallback(() => {
    exportLotsCompleteDatabaseToExcel({
      lots: allLots,
      projectsMap,
      contractsById,
      allotmentsById,
      constructionTypeNamesById,
    });
  }, [allLots, projectsMap, contractsById, allotmentsById, constructionTypeNamesById]);

  const handleDownloadLotPdf = useCallback(
    async (lot) => {
      if (!lot) return;
      const project = getLotResolvedProject(lot, projectsMap);
      const contract = lot?.contract || contractsById?.[lot?.uuid_contract] || null;
      const contractLabel = getLotResolvedContractNumber(lot, contractsById);
      const subscribers = Array.isArray(contract?.subscribers) ? contract.subscribers : [];
      const constructionTypeName = getLotResolvedConstructionType(lot, constructionTypeNamesById);

      await exportSingleLotToPdf(lot, {
        project,
        contract: contractLabel !== '-' ? contract : null,
        subscribers,
        allotmentName: allotmentsById?.[lot?.uuid_allotment]?.name || '-',
        constructionTypeName,
        progressValue: lot?.project_progress_summary?.completion_percentage ?? 0,
        qualityValue: lot?.project_quality_summary?.completion_percentage ?? 0,
      });
    },
    [projectsMap, contractsById, allotmentsById, constructionTypeNamesById]
  );

  const isTableView = view === 'table';
  const isDashboardView = view === 'dashboard';
  const isGeneralDashboardView = view === 'general-dashboard';

  return (
    <Box>
      <FormTitleBox
        title="Obra | Lotes | Lotes"
        showButton={isTableView}
        buttonText="Agregar Lote"
        buttonIcon={HomeWorkIcon}
        onButtonClick={handleOpenDialog}
        buttonProps={{ disabled: !canEditLots }}
        showSearch={isTableView}
        searchLabel="Buscar lote"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
      />

      <LotsHeaderControls
        allotments={allotments}
        selectedAllotment={selectedAllotment}
        onSelectAllotment={handleSelectAllotment}
        view={view}
        onViewChange={setView}
        onDownloadFullDatabase={handleDownloadFullDatabase}
        downloadDisabled={isAllLotsLoading || allLots.length === 0}
        downloadLoading={isAllLotsLoading}
        showTableFilters={isTableView}
        referenceStatusOptions={referenceStatusOptions}
        selectedReferenceStatus={selectedReferenceStatus}
        onReferenceStatusChange={handleReferenceStatusChange}
        contractOptions={contractOptions}
        selectedContractFilter={selectedContractFilter}
        onContractFilterChange={handleContractFilterChange}
      />

      {isGeneralDashboardView ? (
        <LotsDashboardView
          lots={allLots}
          projectsMap={projectsMap}
          allotmentName="Todos los loteos"
          monthlyProgressOverview={monthlyProgressOverview}
          monthlyProgressOverviewLoading={isMonthlyProgressOverviewLoading}
        />
      ) : selectedAllotment ? (
        view === 'plan' ? (
          <LotsPlanView allotment={selectedAllotmentData} canEditLots={canEditLots} />
        ) : isDashboardView ? (
          <LotsDashboardView
            lots={selectedAllotmentLots}
            projectsMap={projectsMap}
            allotmentName={selectedAllotmentData?.name || ''}
            monthlyProgressOverview={monthlyProgressOverview}
            monthlyProgressOverviewLoading={isMonthlyProgressOverviewLoading}
          />
        ) : (
          <Box>
            <LotsTableView
              data={data}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canEditLots={canEditLots}
              LoadingComponent={TableBodySkeleton}
              projectsMap={projectsMap}
              contracts={contracts}
              constructionTypes={constructionTypes}
              onDownloadPdf={handleDownloadLotPdf}
            />
            <PaginationTable
              totalPages={totalPages}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        )
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Seleccioná un loteo para ver los lotes.
          </Typography>
        </Box>
      )}

      <LotDialog
        open={dialogOpen && !editMode}
        onClose={handleCloseDialog}
        editMode={false}
        selectedLot={selectedLot}
        onSave={handleSave}
        canEditLots={canEditLots}
        contracts={contracts}
        constructionTypes={constructionTypes}
        allLots={allLots}
      />

      <LotDialog
        open={dialogOpen && editMode}
        onClose={handleCloseDialog}
        editMode={true}
        selectedLot={selectedLot}
        contracts={contracts}
        constructionTypes={constructionTypes}
        allLots={allLots}
        project={selectedProject}
        contract={selectedContract}
        subscribers={subscribers}
        onSave={handleUpdateLot}
        canEditLots={canEditLots}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Eliminar Lote"
        message={`¿Está seguro que desea eliminar el lote "${lotToDelete?.lot_number}"?`}
        confirmText="Eliminar lote"
        confirmDisabled={!canEditLots}
      />
    </Box>
  );
};

export default LotsTable;
